import React, {
  BaseSyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  JSX,
} from 'react';
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
export * from './types';
import InteractiveTooltip from 'src/components/InteractiveTooltip';
import {
  TooltipComponentsSx,
  TooltipOffsets,
} from 'src/components/InteractiveTooltip/domain';
import groupBy from 'lodash/groupBy';
import { QuickFiltersSection } from './QuickFiltersSection';
import TriggerButton from './TriggerButton';
import { ControlFilters, FiltersProps } from './types';
import { MainFiltersSection } from './MainFiltersSection';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { ClearButton } from './ClearButton';
import pickBy from 'lodash/pickBy';
import keys from 'lodash/keys';
import theme from 'src/theme';
import SearchIcon from '@mui/icons-material/SearchOutlined';

const componentsSx: TooltipComponentsSx = {
  arrow: {
    color: theme.palette.background.paper,
    fontSize: '1rem',
    '&::before': {
      border: `1px solid ${theme.palette.dividerLight}`,
      BorderTop: 'none',
      boxShadow: theme.shadows[0],
    },
  },
};

type CustomContextType = {
  submittedFields: string[];
  formIsSubmittedOnFieldChange: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  onChangeSubmit: (
    e?: BaseSyntheticEvent<Record<string, any>>,
  ) => Promise<void>;
};
const DEFAULT_TOOLTIP_OFFSETS: TooltipOffsets = { x: -8, y: -42 };

export const CustomFiltersCtx = React.createContext<CustomContextType>({
  submittedFields: [],
  formIsSubmittedOnFieldChange: false,
  setIsSubmitting: () => null,
  onChangeSubmit: () => new Promise(() => null),
});

export function useFormCustomContext(): CustomContextType {
  return useContext(CustomFiltersCtx);
}

/**
 * Filters` props
 * @param formIsSubmittedOnFieldChange - Optional.Defines if the form doesn't have a Submit button and is submitted on field change (false by default).
 * @param filtersTitle - Title for the filters section.
 * @param isParentOpen - Optional. True by default. If a parent element is open. Relevant for cases where a parent is a dynamic container (for example - MasterView).
 * @param isDisabled - Indicates whether the filters are disabled.
 * @param isQuickFiltersDisabled - Indicates whether quick filters are disabled.
 * @param isThroughPortal - Indicates whether the filters are rendered through a portal.
 * @param filtersConfig - Configuration for the filters.
 * @param initialValues - Initial values for the filters.
 * @param valuesResetTo - Values to reset the form to the clear state.
 * We cannot use initialValues, because it might consist already set filters from the previous iteration.
 * @param submittingMessage - Message to show while submitting.
 * @param setSubmittingMessage - Update Message to show while submitting.
 * @param tooltipOffsets - Offsets for the filters tooltip position.
 * @param renderTrigger - Function to render the trigger element.
 * @param onSubmit - Function to handle form submission.
 * @param onResetFilters - Function to reset the filters to valuesResetTo.
 */
export const Filters = <T extends string>({
  formIsSubmittedOnFieldChange = false,
  filtersTitle,
  isDisabled,
  isQuickFiltersDisabled,
  isThroughPortal = true,
  isParentOpen = true,
  filtersConfig,
  initialValues,
  valuesResetTo,
  submittingMessage,
  setSubmittingMessage,
  tooltipOffsets,
  renderTrigger,
  onSubmit,
  onResetFilters,
}: FiltersProps<T>): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedFields, setSubmittedFields] = useState<string[]>([]);

  const methods = useForm({
    defaultValues: async () => initialValues,
    mode: 'onChange',
  });
  const { handleSubmit, reset } = methods;

  const groupedFilters = useMemo(
    () => groupBy(filtersConfig.main, 'groupBy'),
    [filtersConfig.main],
  );

  // initial values are the result of an asynchronous action,
  // so we need to update the form values after the form has been initialized
  useEffect(() => {
    reset(initialValues);
  }, [reset, initialValues]);

  // close filters if the parent container is closed
  useEffect(() => {
    if (!isParentOpen) {
      setIsSubmitting(false);
      setIsOpen(false);
    }
  }, [isParentOpen]);

  const manageSubmittedFieldsState = useCallback((fieldsObj: FieldValues) => {
    const fieldsWithValues: ControlFilters = pickBy(
      fieldsObj,
      (value) => value,
    );
    setSubmittedFields(keys(fieldsWithValues));

    return fieldsWithValues;
  }, []);

  const handleSetFilters = useCallback(
    (formData: FieldValues) => {
      setSubmittingMessage?.('');
      const fieldsWithValues = manageSubmittedFieldsState(formData);
      onSubmit(fieldsWithValues);
      setIsSubmitting(false);
    },
    [setSubmittingMessage, manageSubmittedFieldsState, onSubmit],
  );

  const handleResetFilters = useCallback(() => {
    onResetFilters();
    setIsSubmitting(false);
  }, [onResetFilters]);

  useEffect(() => {
    manageSubmittedFieldsState(initialValues as FieldValues);
  }, [initialValues, manageSubmittedFieldsState]);

  return (
    <CustomFiltersCtx.Provider
      value={{
        submittedFields,
        formIsSubmittedOnFieldChange,
        setIsSubmitting,
        onChangeSubmit: handleSubmit(handleSetFilters),
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          display="flex"
          flexWrap="nowrap"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <MainFiltersSection<T> groupedFilters={groupedFilters} />
          <ClearButton
            valuesResetTo={valuesResetTo}
            onClick={handleResetFilters}
          />
          {/* <InteractiveTooltip
            componentsSx={componentsSx}
            tooltipOffsets={relevantTooltipOffsets}
            targetPlacement="right-start"
            overrideIsOpen={isOpen}
            setOverrideIsOpen={setIsOpen}
            isThroughPortal={isThroughPortal}
            Trigger={
              renderTrigger ? (
                renderTrigger({
                  isOpen,
                  isDisabled,
                  onClick: handleToggleFilters,
                })
              ) : (
                <TriggerButton
                  isFiltersExpanded={isOpen}
                  isDisabled={isDisabled}
                  onClick={handleToggleFilters}
                />
              )
            }
            Contents={
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: ({ palette }) => palette.background.paper,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                  gap: 1,
                  maxWidth: '500px',
                  borderRadius: ({ spacing }) => spacing(0.75),
                  boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 8px 0px',
                }}
              >
                {filtersTitle ? (
                  <Box width="100%">
                    <Typography
                      sx={{
                        userSelect: 'none',
                        color: ({ palette }) => palette.primary.main,
                      }}
                      variant="body1"
                      fontWeight={500}
                      p={1.75}
                      pt={1.75}
                    >
                      {filtersTitle}
                    </Typography>
                    <Divider sx={{ opacity: 0.6 }} flexItem />
                    {!filtersConfig.quick && isSubmitting ? (
                      <LinearProgress />
                    ) : null}
                    {!filtersConfig.quick &&
                    isSubmitting &&
                    !!submittingMessage ? (
                      <Typography
                        sx={{
                          userSelect: 'none',
                          color: ({ palette }) => palette.primary.main,
                        }}
                        variant="subtitle2"
                        fontWeight={500}
                        px={1.75}
                        pt={1}
                      >
                        {submittingMessage}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
                {filtersConfig.quick ? (
                  <Box width="100%">
                    <QuickFiltersSection<T>
                      isDisabled={isQuickFiltersDisabled || isDisabled}
                      options={filtersConfig.quick}
                    />
                    <Divider light flexItem />
                    {isSubmitting ? <LinearProgress /> : null}
                    {isSubmitting && !!submittingMessage ? (
                      <Typography
                        sx={{
                          userSelect: 'none',
                          color: ({ palette }) => palette.primary.main,
                        }}
                        variant="subtitle2"
                        fontWeight={500}
                        px={1.75}
                        pt={1}
                      >
                        {submittingMessage}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}

                {filtersConfig?.main ? (
                  <MainFiltersSection<T> groupedFilters={groupedFilters} />
                ) : null}
                <Box
                  key="controlsActions"
                  display="flex"
                  flexDirection="column"
                  flexWrap="nowrap"
                  width="100%"
                >
                  <Divider sx={{ opacity: 0.6 }} flexItem />
                  <Box
                    display="flex"
                    gap={2}
                    sx={{ p: ({ spacing }) => spacing(1.5, 2) }}
                    justifyContent="flex-end"
                  >
                    <ClearButton
                      valuesResetTo={valuesResetTo}
                      onClick={handleResetFilters}
                    />
                    {!formIsSubmittedOnFieldChange ? (
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SearchIcon />}
                        size="small"
                        sx={{
                          textTransform: 'none',
                        }}
                        onClick={handleSubmit(handleSetFilters)}
                      >
                        חפש
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              </Paper>
            }
          /> */}
        </Box>
      </FormProvider>
    </CustomFiltersCtx.Provider>
  );
};
